// Token half of the embed protocol. The parent owns auth entirely — the child can neither mint a
// token nor redirect to Keycloak — so everything here is receive-and-wait.

// Unbounded, a host that never answers leaves initialize() pending. Coincides with Validate's
// own splash-reveal timeout; don't raise it past theirs.
const EMBED_TOKEN_TIMEOUT_MS = 15_000;

let embedToken: string | null = null;
const tokenWaiters = new Set<(token: string | null) => void>();
const tokenListeners = new Set<(token: string) => void>();

// sessionStorage as well as a query param: the cold load is decided before `init` arrives.
const STALL_KEY = 'hc-extract-embed-auth-stall';

const authStalled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  try {
    if (sessionStorage.getItem(STALL_KEY) === 'true') return true;
  } catch {
    /* empty */
  }
  try {
    return new URLSearchParams(window.location.search).get('embedAuthStall') === '1';
  } catch {
    return false;
  }
};

export const devSetAuthStall = (on: boolean) => {
  if (!import.meta.env.DEV) return;
  try {
    if (on) sessionStorage.setItem(STALL_KEY, 'true');
    else sessionStorage.removeItem(STALL_KEY);
  } catch {
    /* empty */
  }
};

export const devIsAuthStalled = (): boolean => authStalled();

const deliverToken = (token: string) => {
  embedToken = token;
  const waiters = [...tokenWaiters];
  tokenWaiters.clear();
  waiters.forEach((settle) => settle(token));
  tokenListeners.forEach((listener) => listener(token));
};

export const setEmbedToken = (token: string) => {
  if (authStalled()) return;
  deliverToken(token);
};

export const getEmbedToken = (): string | null => embedToken;

export const awaitEmbedToken = (timeoutMs = EMBED_TOKEN_TIMEOUT_MS): Promise<string | null> => {
  if (embedToken) return Promise.resolve(embedToken);
  return new Promise<string | null>((resolve) => {
    const settle = (token: string | null) => {
      clearTimeout(timer);
      tokenWaiters.delete(settle);
      resolve(token);
    };
    const timer = setTimeout(() => settle(null), timeoutMs);
    tokenWaiters.add(settle);
  });
};

/** Fires on late tokens too — the child can't mint one, so this is its only recovery path. */
export const onEmbedToken = (handler: (token: string) => void): (() => void) => {
  tokenListeners.add(handler);
  return () => {
    tokenListeners.delete(handler);
  };
};

export const devDeliverEmbedToken = (token: string) => {
  if (!import.meta.env.DEV) return;
  deliverToken(token);
};
